(function () {
  const STAR_PATH =
    'M4.53627 0L5.65787 2.99095L9.07292 3.05579L6.351 4.96893L7.34009 8L4.53588 6.1911L1.73244 8L2.72153 4.96893L0 3.05579L3.41505 2.99131L4.53627 0Z';

  function getFilledStarCount(rating) {
    const rounded = Math.round((Number(rating) || 0) * 10) / 10;
    const fullStars = Math.floor(rounded);
    const fraction = rounded - fullStars;
    return fraction >= 0.7 ? Math.min(5, fullStars + 1) : Math.min(5, fullStars);
  }

  function renderStarsHtml(rating) {
    const filledCount = getFilledStarCount(rating);
    return Array.from({ length: 5 }, (_, index) => {
      const filled = index < filledCount;
      return `<span class="product-card-reviews__star product-card-reviews__star--${filled ? 'full' : 'empty'}">
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
          <path d="${STAR_PATH}" fill="${filled ? 'black' : '#E5E5E5'}"></path>
        </svg>
      </span>`;
    }).join('');
  }

  function parseNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function getReviewProductIds(card) {
    const fromAttr = (card.dataset.reviewProductIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    if (fromAttr.length) return fromAttr;
    return card.dataset.productId ? [card.dataset.productId] : [];
  }

  function getJudgemeWidgetSummary(productId) {
    const widgets = window.jdgm?.data?.reviewWidget;
    if (!widgets || productId == null) return null;

    const widget = widgets[productId] || widgets[String(productId)] || widgets[Number(productId)];
    if (!widget) return null;

    const average = parseNumber(widget.average_rating ?? widget.avg_rating);
    const count = parseNumber(widget.number_of_reviews);
    if (count == null) return null;

    return { average: average || 0, count };
  }

  function getBestJudgemeSummary(productIds) {
    let best = null;
    productIds.forEach((id) => {
      const summary = getJudgemeWidgetSummary(id);
      if (!summary || summary.count <= 0) return;
      if (
        !best ||
        summary.count > best.count ||
        (summary.count === best.count && summary.average > best.average)
      ) {
        best = summary;
      }
    });
    return best;
  }

  function getBadgeSummary(card) {
    const badge =
      card.querySelector('.jdgm-prev-badge[data-average-rating], .jdgm-prev-badge[data-number-of-reviews]') ||
      card.querySelector('[data-average-rating][data-number-of-reviews]');
    if (!badge) return null;

    const average = parseNumber(badge.getAttribute('data-average-rating'));
    const count = parseNumber(badge.getAttribute('data-number-of-reviews'));
    if (count == null) return null;

    return { average: average || 0, count };
  }

  function combineSummary(jmRating, jmCount, metaStars, metaCount) {
    const safeJmCount = Math.max(0, Math.round(jmCount || 0));
    const safeMetaCount = Math.max(0, Math.round(metaCount || 0));
    const combinedCount = safeJmCount + safeMetaCount;
    if (combinedCount <= 0) return { average: 0, count: 0 };

    const combinedStars = Number(jmRating || 0) * safeJmCount + Number(metaStars || 0);
    return {
      average: combinedStars / combinedCount,
      count: combinedCount,
    };
  }

  function applySummary(card, average, count) {
    const starsEl = card.querySelector('.product-card-reviews__stars');
    const countEl = card.querySelector('.product-card-reviews__count');
    const safeCount = Math.max(0, Math.round(count || 0));
    const safeAverage = Number(average) || 0;

    card.dataset.averageRating = String(safeAverage);
    card.dataset.numberOfReviews = String(safeCount);
    card.classList.toggle('product-card-reviews--empty', safeCount <= 0);
    if (safeCount <= 0) {
      card.setAttribute('aria-hidden', 'true');
      card.removeAttribute('role');
      card.removeAttribute('aria-label');
    } else {
      card.removeAttribute('aria-hidden');
      card.setAttribute('role', 'img');
      card.setAttribute(
        'aria-label',
        `Rated ${Math.round(safeAverage * 10) / 10} out of 5 from ${safeCount} reviews`
      );
    }

    if (starsEl) starsEl.innerHTML = renderStarsHtml(safeAverage);
    if (countEl) countEl.textContent = `(${safeCount})`;
  }

  function updateCard(card) {
    const productIds = getReviewProductIds(card);
    const liquidJmCount = parseNumber(card.dataset.jmCount) || 0;
    const liquidJmRating = parseNumber(card.dataset.jmRating) || 0;
    const metaCount = parseNumber(card.dataset.metaCount) || 0;
    const metaStars = parseNumber(card.dataset.metaStars) || 0;
    const groupMatched = card.dataset.reviewGroupMatched === 'true';
    const groupCount = parseNumber(card.dataset.reviewGroupCount) || 0;
    const groupRating = parseNumber(card.dataset.reviewGroupRating) || 0;
    const liquidCount = parseNumber(card.dataset.liquidCount || card.dataset.numberOfReviews) || 0;
    const liquidAverage = parseNumber(card.dataset.averageRating) || 0;

    if (groupMatched) {
      if (groupCount <= 0) {
        applySummary(card, 0, 0);
        card.hidden = true;
        return;
      }
      const rating = groupRating > 0 ? groupRating : liquidAverage || liquidJmRating;
      applySummary(card, rating, groupCount);
      return;
    }

    const fromWidgets = getBestJudgemeSummary(productIds);
    const fromBadge = getBadgeSummary(card);
    const liveJm = fromWidgets || fromBadge;

    let jmCount = liquidJmCount;
    let jmRating = liquidJmRating;
    if (liveJm && liveJm.count >= liquidJmCount) {
      jmCount = liveJm.count;
      jmRating = liveJm.average;
    }

    const combined = combineSummary(jmRating, jmCount, metaStars, metaCount);
    const nextCount = Math.max(combined.count, liquidCount);
    const nextAverage = combined.count >= liquidCount && combined.count > 0 ? combined.average : liquidAverage;
    applySummary(card, nextAverage, nextCount);
  }

  function updateProductCardReviews(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const cards = scope.matches?.('[data-product-card-reviews]')
      ? [scope]
      : Array.from(scope.querySelectorAll('[data-product-card-reviews]'));
    cards.forEach(updateCard);
  }

  window.updateProductCardReviews = updateProductCardReviews;

  function bindJudgeMeEvents() {
    document.addEventListener('jdgm.done', () => updateProductCardReviews());
    document.addEventListener('jdgm.previewBadgeLoaded', () => updateProductCardReviews());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => updateProductCardReviews());
  } else {
    updateProductCardReviews();
  }

  bindJudgeMeEvents();
})();
